import { authFetch } from '@/api/request';
import { useState, useEffect } from 'react';
import { Search, Sparkles, X } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ParticleItem { id: string; name: string; previewUrl: string; frameCount: number; tags: string[]; source: string; }

interface ParticleCatalogProps { open: boolean; onClose: () => void; onSelect?: (name: string, description: string) => void; }

export function ParticleCatalog({ open, onClose, onSelect }: ParticleCatalogProps) {
  const [particles, setParticles] = useState<ParticleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    authFetch(`/api/playable/particles${search ? `?q=${search}` : ''}`)
      .then(r => r.json()).then(d => setParticles(d.particles ?? [])).catch(() => setParticles([]))
      .finally(() => setLoading(false));
  }, [open, search]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-neon-cyan" />
            <span className="text-sm font-semibold text-foreground">粒子特效库</span>
          </div>
          <button onClick={onClose} className="icon-btn rounded p-1"><X size={16} /></button>
        </div>

        <div className="border-b border-border/30 px-4 py-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input className="input-neon w-full py-2 pl-8 pr-3 text-sm" placeholder="搜索粒子特效名称或标签..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-xs text-muted-foreground">加载中...</div>
          ) : particles.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-xs text-muted-foreground">暂无粒子特效</div>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {particles.map(p => (
                <button key={p.id} onClick={() => { onSelect?.(p.name, p.tags.join(', ')); onClose(); }}
                  className="glass-card card-lift cursor-pointer overflow-hidden text-left transition-all hover:border-primary/30">
                  <div className="aspect-square bg-muted/30 flex items-center justify-center">
                    {p.previewUrl ? <img src={p.previewUrl} alt={p.name} className="h-full w-full object-contain p-2" /> :
                      <Sparkles size={28} className="text-muted-foreground/30" />}
                  </div>
                  <div className="border-t border-border/20 p-2">
                    <p className="text-xs font-medium text-foreground">{p.name}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {p.tags?.slice(0, 3).map(t => <span key={t} className="rounded bg-muted/30 px-1 py-0.5 text-[9px] text-muted-foreground">{t}</span>)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
